import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  CreditCard,
  Coins,
  RefreshCcw,
  ShoppingCart,
  ShieldCheck,
  Headset,
  Search,
  Scale,
  MessageSquare,
  Zap,
  Receipt,
  Plug,
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import ResponsiveAccordion from "@/components/ResponsiveAccordion";
import StripeCostCheckForm from "@/components/StripeCostCheckForm";
import GoogleTrustPill from "@/components/GoogleTrustPill";
import HeroVisualCard from "@/components/HeroVisualCard";
import analytics from "@/utils/analytics";

const PATH = "/landing/stripe-cost-check";

// Destination pages open in a new tab per the 10 Sep 2026 pack; in-page
// anchors keep scrolling the current page.
const STRIPE_PRICING_URL = "https://stripe.com/gb/pricing";
const STRIPE_FEES_GUIDE_URL = "https://candopayments.com/blog/using-stripe-check-your-payment-costs/";
const LOGO_URL = "https://cdn.candopayments.com/logos/cando_long_colour_web_transparent.svg";

// Search metadata verbatim from the 9 Sep 2026 copy pack, which supplies one
// consistent set for <title>, description and their Open Graph twins. Flipping
// this page to indexable stays a one-line change to `robots` below.
const META_TITLE = "Free Stripe Cost Review | CanDo Payments";
const META_DESCRIPTION =
  "See how your Stripe charges add up. Get a free CanDo Payments review of your costs and payment needs, with no obligation to switch.";
const OG_IMAGE =
  "https://cdn.candopayments.com/marketing/campaigns/stripe-cost-check-2026/stripe-cost-check-hero-1200x630-v1.png";

// STAGING GATE: publication, the final canonical path and the GA4/GTM
// treatment are a separately agreed scope, and enquiry routing has to be
// confirmed and tested before live collection. The 9 Sep 2026 copy pack
// explicitly retains this ("Retain the agreed indexing policy; this copy pack
// does not change it"), so the page stays noindex and is deliberately absent from
// src/sitemap-routes.json. It is prerendered (package.json reactSnap.include)
// only so the staging preview serves real markup.
const ROBOTS = "noindex, nofollow";

// Single source for the visible accordion AND the FAQPage schema (via
// SEOHead's `faqs` prop) so they can never drift. Copy is verbatim from the
// 9 Sep 2026 pack — deliberately non-committal about savings, and the
// "we'll tell you to stay with Stripe" answer is the point of the offer, not
// a hedge to be trimmed.
const FAQS = [
  {
    q: "Who can request a review?",
    a: "Businesses using Stripe for online payments. You're welcome to ask whether you already work with CanDo or are considering us for the first time.",
  },
  {
    q: "Is it free?",
    a: "Yes. The review is free and you're under no obligation to change provider.",
  },
  {
    q: "Could I reduce my payment costs?",
    a: "That depends on your current agreement, the payments you take and the services you need. We'll look at your own figures before discussing any potential saving.",
  },
  {
    q: "Will you tell me if I should stay with Stripe?",
    a: "Yes. If staying with Stripe is the better overall choice, we'll say so. Any decision to change remains yours.",
  },
  {
    q: "What do I need to send?",
    a: "Just your contact details to begin with. Sam will confirm which figures would help and how to share any documents securely.",
  },
];

const COVERAGE = [
  [Coins, "Your current charges", "We'll look at percentage fees, fixed charges and any other payment costs in your agreement."],
  [
    CreditCard,
    "The payments you take",
    "The number and value of transactions, along with the types of cards your customers use, help us compare the same activity.",
  ],
  [
    RefreshCcw,
    "How the service works for you",
    "We'll consider payouts, refunds, reporting and the support your team needs.",
  ],
  [
    ShoppingCart,
    "What a change would involve",
    "Your checkout and existing integrations matter too. We'll discuss the practical work involved in moving to a different setup.",
  ],
];

const STEPS = [
  [Search, "Get in touch with Sam", "Leave your contact details. Sam will ask about your current setup and what you'd like to check."],
  [
    Scale,
    "Review your figures",
    "We'll agree which information is useful and how to share it securely, then compare your costs and payment needs with a CanDo option.",
  ],
  [
    MessageSquare,
    "Understand your options",
    "We'll explain how the costs compare and what a change would involve, so you can decide whether to explore a CanDo option or keep your current setup.",
  ],
];

// Why consider CanDo — Gary's confirmed offer terms. Deliberately hedged:
// settlement is "we'll confirm the applicable terms", not a guarantee, and
// nothing here promises funds are never held, accounts never close, or that
// every integration is instant.
const BENEFITS = [
  [Zap, "Next-day settlement", "Receive your funds the next day. We'll confirm the applicable settlement terms for your business."],
  [
    Receipt,
    "Gross payments, monthly fees",
    "We pay your funds gross and bill processing fees monthly, keeping fees separate from your payouts for easier reconciliation.",
  ],
  [ShieldCheck, "Underwriting before you start", "We assess your application upfront, before you start taking payments."],
  [
    Plug,
    "Plugins and bespoke integrations",
    "We have ready-made plugins for WooCommerce, PrestaShop, BigCommerce, OpenCart and Magento. An API is available for bespoke websites and app integrations. We'll check your current setup and explain what the move involves.",
  ],
];

// ─── small presentational helpers (site landing-page language) ───────────
function SectionHeading({ eyebrow, title, children, center = true, className = "" }) {
  return (
    <div className={`${center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"} mb-8 ${className}`}>
      {eyebrow && (
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua-dark mb-3">{eyebrow}</p>
      )}
      <h2 className="font-heading text-2xl sm:text-4xl font-bold text-brand-black tracking-tight">{title}</h2>
      {children && <p className="text-base sm:text-lg text-brand-grey leading-relaxed mt-4">{children}</p>}
    </div>
  );
}

function Card({ icon: Icon, title, children, testId }) {
  return (
    <div
      className="bg-white border border-brand-light-grey rounded-2xl p-6 shadow-sm h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
      data-testid={testId}
    >
      {Icon && (
        <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center mb-3">
          <Icon size={19} className="text-brand-aqua-dark" />
        </div>
      )}
      {title && <h3 className="font-heading text-base font-bold text-brand-black mb-1.5">{title}</h3>}
      <p className="text-sm text-brand-grey leading-relaxed">{children}</p>
    </div>
  );
}

// In-page anchor CTA that fires an analytics event before the native jump.
function AnchorCTA({ href, event, variant = "primary", className = "", children, testId }) {
  const base =
    variant === "primary"
      ? "cta-primary text-white"
      : "bg-white border-2 border-brand-aqua text-brand-aqua-dark hover:bg-brand-aqua-light";
  return (
    <a
      href={href}
      onClick={() => event && analytics.landingEvent(event)}
      className={`inline-flex items-center justify-center gap-2 font-semibold rounded-full px-6 py-3.5 text-sm transition-all ${base} ${className}`}
      data-testid={testId}
      data-measurement-owned={event ? "landing" : undefined}
    >
      {children}
    </a>
  );
}

export default function StripeCostCheckPage() {
  const formRef = useRef(null);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const [formOnScreen, setFormOnScreen] = useState(false);
  const viewFired = useRef(false);

  // Page view — uses the existing thin landing emitter only. A dedicated GA4
  // event set for this campaign is a separately agreed scope (campaign brief).
  useEffect(() => {
    if (viewFired.current) return;
    viewFired.current = true;
    analytics.landingEvent("scc_landing_view");
  }, []);

  // Sticky mobile CTA: appears past the hero, hidden while the form is on
  // screen so it never covers the controls (same pattern as
  // PartnerWithUsPage / PartnerLandingPage).
  useEffect(() => {
    const el = formRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => setFormOnScreen(e.isIntersecting)),
      { threshold: 0.05 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolledPastHero(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const showSticky = scrolledPastHero && !formOnScreen;

  return (
    <div className="bg-white min-h-screen">
      <SEOHead
        rawTitle={META_TITLE}
        description={META_DESCRIPTION}
        path={PATH}
        robots={ROBOTS}
        suppressBreadcrumb
        faqs={FAQS}
        ogImage={OG_IMAGE}
        ogImageAlt="CanDo Payments — free Stripe cost review for businesses using Stripe"
      />

      {/* ── Minimal landing header ─────────────────────────────────── */}
      <header
        className="sticky top-0 z-40 bg-white/90 backdrop-blur-sm border-b border-brand-light-grey/60"
        data-testid="scc-landing-header"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-3 h-16">
          <Link to="/" aria-label="CanDo Payments home" className="min-w-0">
            <img src={LOGO_URL} alt="CanDo Payments" width="1217" height="165" className="h-5 sm:h-7 w-auto" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-5 flex-shrink-0">
            <a
              href="tel:+448003682989"
              onClick={() => analytics.clickPhone()}
              className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-brand-aqua-dark hover:text-brand-aqua transition-colors"
              data-testid="scc-header-phone"
              data-measurement-owned="contact"
            >
              <Phone size={15} /> 0800 368 2989
            </a>
            {/* Label shortens below sm so the pill can't wrap over the logo. */}
            <AnchorCTA
              href="#cost-check-form"
              event="scc_cta_click"
              className="!px-4 sm:!px-5 !py-2.5 whitespace-nowrap"
              testId="scc-header-cta"
            >
              <span className="sm:hidden">Free cost review</span>
              <span className="hidden sm:inline">Request your free cost review</span>
            </AnchorCTA>
          </div>
        </div>
      </header>

      <main>
        {/* ── Hero — text-led (the campaign brief sanctions a clean text-led
            hero for the first build while a 1600x900 e-commerce image is
            selected; drop it in here as a full-bleed background later). ── */}
        <section className="relative overflow-hidden bg-gradient-to-b from-brand-aqua-light/60 via-white to-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-4">
                  For businesses using Stripe
                </p>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black tracking-tight leading-tight">
                  Using Stripe? <span className="text-brand-aqua-dark">Know your payment costs.</span>
                </h1>
                <p className="text-base sm:text-lg text-brand-grey leading-relaxed mt-5">
                  Review your Stripe costs with CanDo. UK domestic consumer cards are 1% + 15p per transaction, business
                  cards 2% + 15p and international cards 2.99% + 15p.
                </p>
                <p className="text-base sm:text-lg text-brand-grey leading-relaxed mt-4">
                  Get next-day settlement, with funds paid gross and processing fees billed monthly for easier
                  accounting.
                </p>
                <p className="text-base sm:text-lg text-brand-black font-semibold leading-relaxed mt-4">
                  A free review. No obligation to switch. We&rsquo;ll confirm the full quote and settlement terms for
                  your business.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mt-7">
                  <AnchorCTA href="#cost-check-form" event="scc_cta_click" testId="scc-hero-primary">
                    Request your free cost review <ArrowRight size={16} />
                  </AnchorCTA>
                  <AnchorCTA
                    href="#how-it-works"
                    event="scc_how_it_works_click"
                    variant="secondary"
                    testId="scc-hero-secondary"
                  >
                    See how the review works
                  </AnchorCTA>
                </div>
                <div className="flex flex-wrap items-center gap-2.5 mt-6">
                  <span className="inline-flex items-center gap-1.5 bg-white border border-brand-light-grey rounded-full px-3.5 py-1.5 text-xs sm:text-sm font-semibold text-brand-black">
                    <ShieldCheck size={15} className="text-brand-aqua-dark flex-shrink-0" />
                    <span>Certified B Corporation&trade;</span>
                  </span>
                  <GoogleTrustPill variant="inline" testId="scc-google-pill" />
                  <span className="inline-flex items-center gap-1.5 bg-white border border-brand-light-grey rounded-full px-3.5 py-1.5 text-xs sm:text-sm font-semibold text-brand-black">
                    <Headset size={15} className="text-brand-aqua-dark flex-shrink-0" /> Real UK support
                  </span>
                </div>
              </div>
              <HeroVisualCard
                icon={Search}
                accentIcons={[CreditCard, Coins, ShoppingCart]}
                accentLabels={["Your charges", "Payments you take", "Checkout"]}
                testId="scc-hero-visual"
              />
            </div>
          </div>
        </section>

        {/* ── Review coverage ──────────────────────────────────────── */}
        <section className="py-12 sm:py-16 bg-brand-off-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="What the review covers" title="What are you paying for each payment?">
              Start with the charges. Then look at the service behind them. A useful comparison takes account of what
              works well today and what would need to change.
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {COVERAGE.map(([Icon, title, body]) => (
                <Card key={title} icon={Icon} title={title} testId={`scc-coverage-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                  {body}
                </Card>
              ))}
            </div>

            {/* Stripe pricing context. The October line is deliberately
                conditional ("if Stripe has notified you") — the pack forbids
                implying every Stripe account is affected. The £100 example
                compares UK domestic consumer cards only; do not extend it to
                business or international cards, whose rates differ on both
                sides. Stripe's published standard UK rate was 1.5% + 20p at
                the time of writing, hence £1.70. */}
            <div
              className="max-w-3xl mx-auto mt-10 bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm"
              data-testid="scc-pricing-context"
            >
              <p className="text-sm sm:text-base text-brand-grey leading-relaxed">
                If Stripe has notified you of a price increase this October, now is a good time to review what you pay.
                We&rsquo;ve helped many businesses switch from Stripe following price increases.
              </p>
              <p className="text-sm sm:text-base text-brand-grey leading-relaxed mt-4">
                {"On a £100 UK domestic consumer-card payment, CanDo’s transaction fee is "}
                <span className="font-semibold text-brand-black">&pound;1.15</span>
                {". "}
                <a
                  href={STRIPE_PRICING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => analytics.landingEvent("scc_stripe_pricing_click")}
                  className="text-brand-aqua-dark hover:text-brand-aqua underline underline-offset-2 font-semibold"
                  data-testid="scc-stripe-pricing-link"
                  data-measurement-owned="landing"
                >
                  Stripe&rsquo;s published standard UK fee
                </a>
                {" is "}
                <span className="font-semibold text-brand-black">&pound;1.70</span>. That is 55p less for this example
                transaction, before other charges. Your card mix and agreement affect the overall cost.
              </p>
            </div>
          </div>
        </section>

        {/* ── Why consider CanDo ───────────────────────────────────── */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Why consider CanDo" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {BENEFITS.map(([Icon, title, body]) => (
                <Card key={title} icon={Icon} title={title} testId={`scc-benefit-${title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                  {body}
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ── Three-step process ───────────────────────────────────── */}
        <section id="how-it-works" className="py-12 sm:py-16 bg-white scroll-mt-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="Three simple steps" title="How your review works" />
            <ol className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="scc-steps">
              {STEPS.map(([Icon, title, body], i) => (
                <li
                  key={title}
                  className="relative bg-white border border-brand-light-grey rounded-2xl p-6 shadow-sm h-full"
                  data-testid={`scc-step-${i + 1}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className="w-9 h-9 rounded-full bg-brand-aqua-dark text-white font-heading font-bold text-sm flex items-center justify-center flex-shrink-0"
                      aria-hidden="true"
                    >
                      {i + 1}
                    </span>
                    <Icon size={19} className="text-brand-aqua-dark flex-shrink-0" aria-hidden="true" />
                  </div>
                  <h3 className="font-heading text-base font-bold text-brand-black mb-1.5">{title}</h3>
                  <p className="text-sm text-brand-grey leading-relaxed">{body}</p>
                </li>
              ))}
            </ol>
            {/* Repeat of the primary CTA above the form (campaign brief). */}
            <div className="flex flex-col items-center gap-3 mt-9">
              <AnchorCTA href="#cost-check-form" event="scc_cta_click" testId="scc-steps-primary">
                Request your free cost review <ArrowRight size={16} />
              </AnchorCTA>
              <p className="text-sm text-brand-grey">A free review. No obligation to switch.</p>
            </div>

            {/* Placed after the process and away from the main hero button, as
                the pack specifies, so it supports rather than competes with
                the primary CTA. */}
            <p
              className="max-w-3xl mx-auto text-center text-sm text-brand-grey leading-relaxed mt-8"
              data-testid="scc-figures-help"
            >
              {"Not sure where to find your figures? Our guide explains "}
              <a
                href={STRIPE_FEES_GUIDE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.landingEvent("scc_fees_guide_click")}
                className="text-brand-aqua-dark hover:text-brand-aqua underline underline-offset-2 font-semibold"
                data-testid="scc-fees-guide-link"
                data-measurement-owned="landing"
              >
                how to find your Stripe fees and download your transactions
              </a>
              . You don&rsquo;t need to gather everything before getting in touch.
            </p>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────── */}
        <section className="py-12 sm:py-16 bg-brand-off-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Frequently asked questions" />
            <ResponsiveAccordion items={FAQS} testIdPrefix="scc-faq" />
          </div>
        </section>

        {/* ── Repeated CTA + enquiry form ──────────────────────────── */}
        <section ref={formRef} id="cost-check-form" className="py-12 sm:py-16 bg-white scroll-mt-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="Free review. No obligation." title="Request your free Stripe cost review">
              Leave your contact details and Sam will get in touch to discuss your current setup and what you&rsquo;d
              like to check.
            </SectionHeading>
            <div className="bg-white border border-brand-light-grey rounded-2xl shadow-sm p-6 sm:p-8">
              <StripeCostCheckForm />
            </div>
          </div>
        </section>

        {/* ── Closing reassurance ──────────────────────────────────── */}
        <section className="cta-section-bg py-14 sm:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <h2 className="font-heading text-2xl sm:text-4xl font-bold text-white tracking-tight">
              Know where you stand with Stripe.
            </h2>
            <p className="text-white/90 leading-relaxed mt-4">
              Ask us to review your costs before you decide what to do next.
            </p>
            <div className="flex justify-center mt-7">
              <a
                href="#cost-check-form"
                onClick={() => analytics.landingEvent("scc_cta_click")}
                className="inline-flex items-center justify-center gap-2 bg-white text-brand-aqua-dark font-semibold rounded-full px-6 py-3.5 text-sm hover:bg-brand-off-white transition-colors"
                data-testid="scc-final-primary"
                data-measurement-owned="landing"
              >
                Request your free cost review <ArrowRight size={16} />
              </a>
            </div>
            <p className="font-heading text-sm sm:text-base font-semibold text-white/90 tracking-wide mt-7">
              Purpose Beyond Payments
            </p>
          </div>
        </section>
      </main>

      {/* ── Minimal footer ──────────────────────────────────────────── */}
      <footer className="bg-white border-t border-brand-light-grey py-10" data-testid="scc-landing-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-5">
          <img src={LOGO_URL} alt="CanDo Payments" width="1217" height="165" className="h-6 w-auto" />
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm" aria-label="Legal">
            <Link to="/online-payments" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Online Payments
            </Link>
            <Link to="/reviews" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Reviews
            </Link>
            <Link to="/privacy-policy" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Terms
            </Link>
            <Link to="/contact" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Contact
            </Link>
          </nav>
        </div>
        <p className="text-center text-xs text-brand-grey mt-6 px-4">
          {`© ${new Date().getFullYear()} Can Do Payments Ltd. Company No. 11701057. VAT: GB311835329`}
        </p>
      </footer>

      {/* Sticky mobile CTA — appears after the hero, hidden while the form is
          on screen so it never covers it. */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-30 p-3 bg-white/95 backdrop-blur-md border-t border-brand-light-grey transition-transform duration-300 ${
          showSticky ? "translate-y-0" : "translate-y-full"
        }`}
        aria-hidden={!showSticky}
        data-testid="scc-sticky-cta"
      >
        <a
          href="#cost-check-form"
          onClick={() => analytics.landingEvent("scc_mobile_sticky_cta_click")}
          tabIndex={showSticky ? 0 : -1}
          className="cta-primary text-white font-semibold rounded-full px-6 py-3 text-sm flex items-center justify-center gap-2 w-full"
          data-testid="scc-sticky-cta-link"
          data-measurement-owned="landing"
        >
          Request your free cost review <ArrowRight size={16} />
        </a>
      </div>
    </div>
  );
}
